import { createRoom } from '#~/api/agent/index.js';
import { toHexFromId } from '#~/api/index.js';
import { useAuth0 } from '#~/auth/index.js';
import { useSystemHostname } from '#~/agent/hooks.js';
import { CreateRoomDialog } from './dialog.jsx';
import type { CreateRoomDialogProps } from './dialog.jsx';
import { useDialog } from '@studio/design-system/dialog';
import {
  useAnalytics,
  EVENT_CATEGORIES,
  EVENT_NAMES,
  WORKSPACE_PROPERTIES
} from '#~/analytics/index.js';

/**
 * Gets the auto tracking protection state for a specific camera.
 *
 * @returns The auto tracking protection state for the camera.
 */
export const useCreateRoomPrompt = (): {
  open: (organizationId: Uint8Array) => void;
} => {
  const dialog = useDialog<CreateRoomDialogProps>(CreateRoomDialog);
  const { getAccessTokenSilently } = useAuth0();
  const { track } = useAnalytics();
  const hostname = useSystemHostname();

  const open = (organizationId: Uint8Array): void => {
    dialog.open({
      organizationId,
      defaultName: hostname,
      onConfirm: async (name, onSuccess, onError): Promise<void> => {
        try {
          const token = await getAccessTokenSilently();

          // createRoom now returns { id, alreadyExisted } (ENG-4209); this
          // caller only needs the id, so destructure it — behavior unchanged.
          const { id } = await createRoom(name, token, organizationId);

          // Track studio creation event
          const orgIdStr = toHexFromId(organizationId);
          track(EVENT_NAMES.CREATE_STUDIO, EVENT_CATEGORIES.WORKSPACE, {
            [WORKSPACE_PROPERTIES.STUDIO_ID]: id,
            [WORKSPACE_PROPERTIES.STUDIO_NAME]: name,
            ...(orgIdStr && { [WORKSPACE_PROPERTIES.ORG_ID]: orgIdStr })
          });

          onSuccess(id, dialog.close);
        } catch (e) {
          onError();
        }
      },

      onDismiss: () => {
        dialog.close();
      }
    });
  };

  return {
    open
  };
};
